import type { DropOptions, NodeModel, TreeMethods } from '@minoru/react-dnd-treeview';
import { getBackendOptions, getDescendants, MultiBackend, Tree } from '@minoru/react-dnd-treeview';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';

import { ROUTE_PAGE } from '$routes';
import getStore from '$store';
import { Button } from '~components/generic/editor/Button/Button';
import { Chip } from '~components/generic/editor/Chip/Chip';
import { CheckIcon, SaveIcon, WarningIcon } from '~components/icons';
import useLabels from '~hooks/useLabels';
import { useNavigateToPage } from '~hooks/useNavigateToPage';
import { toggleItemInPrimitiveArray } from '~services/immutable';
import { selectAliasFromPath } from '~store/selectors';
import { selectMenuElementById, selectUnfilteredMenuElementById } from '~store/selectors/menu';
import { CustomDragPreview } from './CustomDragPreview';
import { CustomNode } from './CustomNode';
import { CustomPlaceholder } from './CustomPlaceholder';
import ManagePermissionsModal from './ManagePermissionsModal';
import type { CustomData } from './types';
import useTreeData from './useTreeData';
import { ROOT_ID } from './utils';

export default function Navigation({ isEditAllowed }: { isEditAllowed: boolean }) {
  const refTree = useRef<TreeMethods>(null);
  const navigateToPage = useNavigateToPage();
  const { btnSaveNavigation, btnResetNavigation, msgNavigationSaveFailed, msgNavigationSaveSuccessful } = useLabels([
    'msgNavigationSaveSuccessful',
    'msgNavigationSaveFailed',
    'btnResetNavigation',
    'btnSaveNavigation',
  ]);

  const { treeData, updateSuccess, changes, onReset, onSave, isProcessing, addNode, removeNode, renameNode, moveNode } =
    useTreeData(isEditAllowed);
  const hasChanges = useMemo(() => changes.length > 0, [changes]);
  const [openIds, setOpenIds] = useState<(string | number)[]>([ROOT_ID]);
  const [managePermissionsId, setManagePermissionsId] = useState<{ uuid: string; title: string }>();

  // Define: open menu element handler
  const handleLink = useCallback(
    (id: NodeModel['id']) => {
      const state = getStore().getState();
      let element = selectMenuElementById(state, id as string);
      if (!element) {
        // element can be undefined if it is not visible in the navigation
        // then it has to be in the unfiltered menu tree
        element = selectUnfilteredMenuElementById(state, id as string);
      }
      if (element.alias) {
        const pageAlias = selectAliasFromPath(state, element.alias);
        void navigateToPage(ROUTE_PAGE, { pageAlias });
      }
    },
    [navigateToPage],
  );

  // Handler: drop node
  const handleDrop = useCallback(
    (dropTree: NodeModel<CustomData>[], { dragSourceId, dropTargetId }: DropOptions<CustomData>) => {
      // calculate index after drop
      // 1. get children = direct descendants
      const descendants = getDescendants(dropTree, dropTargetId).filter(value => value.parent === dropTargetId);
      // 2. get index of dropped item
      const destIndex = descendants.findIndex(value => value.id === dragSourceId);
      // fail for invalid index
      if (destIndex < 0) return;
      // otherwise move
      moveNode(dropTree, dragSourceId, dropTargetId, destIndex);
    },
    [moveNode],
  );

  // Handler: open / close folder
  const handleToggle = useCallback(id => {
    setOpenIds(openIds => toggleItemInPrimitiveArray(openIds, id));
  }, []);

  // Handler: add page
  const handleAddPage = useCallback(
    (parentId: NodeModel['id']) => {
      addNode(parentId, 'page');
      refTree.current?.open(parentId);
    },
    [addNode],
  );

  // Handler: add group
  const handleAddGroup = useCallback(
    (parentId: NodeModel['id']) => {
      addNode(parentId, 'link');
      refTree.current?.open(parentId);
    },
    [addNode],
  );

  // Handler: show manage permissions dialog
  const handleManagePermissions = useCallback(
    (id: NodeModel['id']) => {
      const node = treeData.find(treeNode => treeNode.id === id);
      if (!node) return;
      const {
        data: { uuid },
        text: title,
      } = node;
      setManagePermissionsId({ uuid, title });
    },
    [treeData],
  );

  // Handler: can drop
  const handleCanDrop = useCallback((_tree: NodeModel<CustomData>[], options: DropOptions<CustomData>) => {
    const { dragSource, dropTargetId, dropTarget } = options;
    // check against false because it can be undefined as well
    if (dropTarget?.data?.permissions?.canCreate === false) return false;
    // check if item itself is editable
    if (dragSource?.data?.editable === false) return false;
    // This allows drag and drop without automatically sorting
    if (dragSource?.parent === dropTargetId) return true;
  }, []);

  // Handler: can drag
  const handleCanDrag = useCallback(
    (node: NodeModel<CustomData>) => {
      if (!isEditAllowed) return false;
      if (isProcessing) return false;
      if (node.id === ROOT_ID) return false;
      return node?.data?.editable || false;
    },
    [isEditAllowed, isProcessing],
  );

  return (
    <>
      <div className="bg-gray rounded border-2 border-solid border-darkgray p-4">
        <DndProvider backend={MultiBackend} options={getBackendOptions()}>
          <Tree
            ref={refTree}
            tree={treeData}
            rootId={0}
            initialOpen={openIds}
            render={(node: NodeModel<CustomData>, { depth, isOpen, hasChild }) => (
              <CustomNode
                node={node}
                depth={depth}
                isOpen={isOpen}
                onToggle={handleToggle}
                hasChild={hasChild}
                onTextChange={renameNode}
                onLink={handleLink}
                onRemove={removeNode}
                onAddPage={handleAddPage}
                onAddGroup={handleAddGroup}
                onManagePermissions={handleManagePermissions}
              />
            )}
            dragPreviewRender={monitorProps => <CustomDragPreview monitorProps={monitorProps} />}
            sort={false}
            insertDroppableFirst={false}
            onDrop={handleDrop}
            canDrop={handleCanDrop}
            canDrag={handleCanDrag}
            dropTargetOffset={10}
            placeholderRender={(node, { depth }) => <CustomPlaceholder node={node} depth={depth} />}
            classes={{
              root: 'h-full',
              listItem: 'my-1 p-0',
              container: 'my-1 p-0',
              draggingSource: 'opacity-30',
              placeholder: 'relative',
            }}
          />
        </DndProvider>
        <ManagePermissionsModal
          open={!!managePermissionsId}
          nodeId={managePermissionsId?.uuid}
          nodeTitle={managePermissionsId?.title}
          onClose={() => setManagePermissionsId(undefined)}
        />
      </div>
      <div className="flex justify-end gap-2 my-4">
        {updateSuccess === true && (
          <div className="">
            <Chip label={msgNavigationSaveSuccessful} status="success" preIcon={<CheckIcon />} />
          </div>
        )}
        {updateSuccess === false && (
          <div className="">
            <Chip label={msgNavigationSaveFailed} status="error" preIcon={<WarningIcon />} />
          </div>
        )}
        <div className="">
          <Button onClick={onReset} disabled={!hasChanges || isProcessing} label={btnResetNavigation} />
        </div>
        <div className="">
          <Button
            onClick={onSave}
            disabled={!hasChanges}
            processing={isProcessing}
            label={btnSaveNavigation}
            icon={<SaveIcon />}
          />
        </div>
      </div>
    </>
  );
}
